import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FormTreeSelect } from "ui-kit/SmartForm";
import { Modal } from "ui-kit/Modal";
import { Button } from "ui-kit/Button";
import { Input } from "ui-kit/PrimitiveControls/Input";
import { Checkbox } from "ui-kit/PrimitiveControls/Checkbox";
import { CheckboxChangeEvent } from "primereact/checkbox";
import { FormattedMessage } from "react-intl";
import { Country } from "types/country";
import { CountryGroups } from "../hooks/useCountryGroup";
import { DEFAULT_COUNTRIES_GROUP} from "./constants";
import { useForm } from "react-final-form";
import { UGCPlatforms } from "./UGCPlatforms";
import IntlStore from "stories/IntlStore";
import { PlatformGroup } from "../hooks/usePlatformGroups";
import { InfoTooltip } from "ui-kit/Tooltip/InfoTooltip";

interface DistributionCountryProps {
  countries: Country[];
  countryGroups: CountryGroups[];
  countryGroupsCreating: boolean;
  createCountryGroup: (name: string, ids: string[]) => Promise<void>;
  removeCountryGroup: (id: string) => Promise<void>;
  UGC_PLATFORM_NAMES: string[]
  platformGroupsCreating: boolean;
  ugcPlatformList: any;
  createPlatformGroup: (name: string, ids: string[]) => Promise<void>;
  removePlatformGroup: (id: string) => Promise<void>;
  data: any
  setIdData: (idData: string[]) => void
  setUgcData: (ugcData: string[]) => void
  platformGroups: PlatformGroup[];
  selectedValues: string[]
  updateCountryGroup: (id: string, name: string, ids: string[]) => Promise<void>;
  ugcPlatforms:any;
  setSelectedValuesCountry: (values: string[]) => void; 
  selectedValuesCountry: string[];
}

type ContinentMapValue = {
  [x: string]: unknown; name: Country["name"], id: Country["id"]
};
type ContinentMap = Record<Country["continent"], ContinentMapValue[]>;

export const DistributionCountry: React.FC<DistributionCountryProps> = props => {
  const { countries, countryGroups, countryGroupsCreating, createCountryGroup, updateCountryGroup, removeCountryGroup, platformGroupsCreating, ugcPlatformList, createPlatformGroup, removePlatformGroup, setUgcData, platformGroups, selectedValues: ugcSelectedValues, selectedValuesCountry, setSelectedValuesCountry } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { change, getState } = useForm();
  const [expandedGroups, setExpandedGroups] = useState<CountryGroups['name'][]>([]);
  const [newCountryGroupName, setNewCountryGroupName] = useState('');
  
  const [value, setValue] = useState<string>('');
  const [openedContinents, setOpenedContinents] = useState<string[]>([])
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [editValue, setEditValue] = useState<string[]>([])
  const [editId, setEditId] = useState<string | null>(null);





  const [newCountryGroupCheckboxes, setNewCountryGroupCheckboxes] = useState<Record<string, boolean>>(() => {
    const values: Record<string, boolean> = {};

    countries.forEach(country => {
      values[country.id] = false;
    });
    return values;
  });

  const isSaveDisabled = newCountryGroupName.length < 3 ||
    newCountryGroupName.length > 30 ||
    !Object.entries(newCountryGroupCheckboxes).some(entry => {
      return entry[1];
    });

  const items = useMemo(() => {
    return [
      { value: DEFAULT_COUNTRIES_GROUP, label: 'All' },
      ...countryGroups.map(group => ({ value: group.id, label: group.name })),
    ];
  }, [countryGroups]);

  const continentMap = useMemo(() => {
    return countries.reduce((result: ContinentMap, country: Country) => {
      const continent = (result[country.continent] || []) as ContinentMapValue[];
      return { ...result, [country.continent]: [...continent, { name: country.name, id: country.id }] };
    }, {} as ContinentMap);
  }, [countries]);

  const openedSet = useMemo(() => new Set(openedContinents), [openedContinents]);

  const countriesItems = useMemo(() => {
    return Object.entries(continentMap).map(([continentName, countries], index) => {
      const total = countries.length;

      const selectedInThis = countries.reduce((acc, c) => {
        const key = `${index}-${c.id}`;
        return acc + (openedSet.has(key) ? 1 : 0);
      }, 0);

      const excluded = Math.max(0, total - selectedInThis);

      return {
        key: `${index}`,
        label: `${continentName || 'No continent'} (${total} total, ${excluded} excluded)`,
        selectable: true,
        children: countries.map((c: any) => ({
          key: `${index}-${c.id}`,
          label: c.name,
        })),
      };
    });
  }, [continentMap, openedSet]);

  ;

  const onToggleGroupAccordion = (group: string) => {
    setExpandedGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    );
  };

  const onSaveCountryGroupCheckboxes = async () => {
  const ids: string[] = [];

  Object.entries(newCountryGroupCheckboxes).forEach(([id, checked]) => {
    if (checked) {
      ids.push(id);
    }
  });

  
  if (editId) {
    if (newCountryGroupName !== editId) {
      updateCountryGroup(editValue[0], newCountryGroupName, ids);
    } else {
      updateCountryGroup(editValue[0], '', ids);
    }
  } else {
    
    if (newCountryGroupName) {
      await createCountryGroup(newCountryGroupName, ids);
    }
  }
};


  

  useEffect(() => {

    if (!editId) return;

    const group = countryGroups.find((g) => g.name === editId);

    if (!group) return;

    const selectedCountryIds = group.countries?.map((c: any) => c.id) || [];

    setNewCountryGroupCheckboxes((prev) => {
      const updated = { ...prev };

      Object.keys(updated).forEach((id) => {
        updated[id] = false;
      });


      selectedCountryIds.forEach((id: string | number) => {
        updated[id] = true;
      });

      return updated;
    });


    setNewCountryGroupName(group.name);

  }, [editId, countryGroups]);

  type CountryWithContinent = Country & { continent: string; iso: string };

  const [filteredCountryList, setFilteredCountryList] = useState<ContinentMap>({});


  const filterCountries = (continentMap: ContinentMap, search: string): ContinentMap => {
    if (!search) return continentMap;

    const searchLower = search.toLowerCase();
    const filteredMap: ContinentMap = {};

    Object.entries(continentMap).forEach(([continent, countries]) => {
      const filteredCountries = countries.filter(country =>
        country.name.toLowerCase().includes(searchLower)
      );
      if (filteredCountries.length > 0) {
        filteredMap[continent] = filteredCountries;
      }
    });

    return filteredMap;
  };

  useEffect(() => {
    const result = filterCountries(continentMap, value);
    setFilteredCountryList(result);
  }, [value, continentMap]);

  // Step 6 requirement: always select ALL countries and do not allow changing them.
  useEffect(() => {
    const allIds = (countries || []).map(c => String(c.id));
    props.setIdData(allIds);
    setSelectedValuesCountry([]);
  }, [countries]);

  return (<>
    <UGCTitle>
      <FormattedMessage
        id="NewRelease.Publish.UGCTitle"
        defaultMessage="UGC platforms. Digital fingerprinting system for copyrighted content"
      />
     <InfoTooltip name="UGC-platform-info"  tooltip="UGC platforms. Digital fingerprinting system for copyrighted content 4.99$"/>
      <span> 4.99$</span>
    </UGCTitle>
    <UGCPlatforms
    ugcPlatforms={props.ugcPlatforms}
      ugcSelectedValues={ugcSelectedValues}
      setUgcData={setUgcData}
      platformGroupsCreating={false}
      platforms={ugcPlatformList}
      ugcPlatformGroups={platformGroups}
      platformGroups={[
        {
          id: "ugc-group",
          name: IntlStore.intl.formatMessage({
            id: "NewRelease.Publish.UGCPlatforms",
            defaultMessage: "UGC Platforms"
          }),
          platforms: ugcPlatformList,
        },
      ]}
    />
    <Container id="countries">
      <SelectsContainer>
        <LockedCountrySelect>
          <LockedLabel>
            <FormattedMessage
              id="NewRelease.Publish.SelectDistributionCountry"
              defaultMessage="Distribution country"
            />
          </LockedLabel>
          <LockedInput aria-disabled="true">
            <FormattedMessage
              id="NewRelease.Publish.AllCountries"
              defaultMessage="The Whole World"
            />
          </LockedInput>
        </LockedCountrySelect>
        {/* <FormSelect
          setEditId={setEditId}
          setEditValue={setEditValue}
          editValue={editValue}
          dropdownOpen={dropdownOpen}
          setDropdownOpen={setDropdownOpen}
          editIcon={true}
          text='Add a new Territory Group'
          top={false}
          styleNone={false}
          name="territoryGroups"
          placeholder="Territory Groups"
          items={items
            .filter((val) => val.value !== 'all')
            .map(item => ({ ...item, label: typeof item.label === 'string' ? item.label : '' }))}
          label={<FormattedMessage id="NewRelease.Publish.SelectMethod" defaultMessage="Select a method" />}
          itemTemplate={(option) => (
            <PlatformOption>
              <PlatformOptionLabel>{option.label}</PlatformOptionLabel>
              {option.value !== DEFAULT_COUNTRIES_GROUP && (
                <>
                  <div onClick={() => removeCountryGroup(option.value)}>
                    <svg>...</svg>
                  </div>
                </>
              )}
            </PlatformOption>
          )}
          tooltip="Tooltip"
          setIsOpen={setIsOpen}
          removePlatformGroup={removeCountryGroup}
          selectedValues={selectedValuesCountry}
          setSelectedValues={setSelectedValuesCountry}
        /> */}
      </SelectsContainer>
      {/* <AddTerritoryGroupButton onClick={() => setIsOpen(true)}>
        <svg width="129" height="14" viewBox="0 0 129 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M23.6289 11L22.8481 8.63232H19.4775L18.6968 11H17.0083L20.2583 1.84033H22.1055L25.3555 11H23.6289ZM21.1406 3.45898L19.8584 7.36914H22.4609L21.1851 3.45898H21.1406ZM28.4785 11.1079C26.7837 11.1079 25.6855 9.76855 25.6855 7.62939C25.6855 5.50293 26.7837 4.15723 28.4785 4.15723C29.4434 4.15723 30.2051 4.65869 30.5605 5.40137H30.5986V1.84033H32.1792V11H30.6304V9.85107H30.605C30.2368 10.6064 29.4624 11.1079 28.4785 11.1079ZM28.9546 5.4585C27.939 5.4585 27.3042 6.30273 27.3042 7.63574C27.3042 8.9751 27.9326 9.81299 28.9546 9.81299C29.9512 9.81299 30.605 8.96875 30.605 7.63574C30.605 6.31543 29.9512 5.4585 28.9546 5.4585ZM36.0195 11.1079C34.3247 11.1079 33.2266 9.76855 33.2266 7.62939C33.2266 5.50293 34.3247 4.15723 36.0195 4.15723C36.9844 4.15723 37.7461 4.65869 38.1016 5.40137H38.1396V1.84033H39.7202V11H38.1714V9.85107H38.146C37.7778 10.6064 37.0034 11.1079 36.0195 11.1079ZM36.4956 5.4585C35.48 5.4585 34.8452 6.30273 34.8452 7.63574C34.8452 8.9751 35.4736 9.81299 36.4956 9.81299C37.4922 9.81299 38.146 8.96875 38.146 7.63574C38.146 6.31543 37.4922 5.4585 36.4956 5.4585ZM47.6802 11H46.0488V3.22412H43.3066V1.84033H50.416V3.22412H47.6802V11ZM52.7075 5.35693C51.8188 5.35693 51.1777 6.03613 51.1143 6.97559H54.2563C54.2246 6.02344 53.6152 5.35693 52.7075 5.35693ZM54.2627 8.98779H55.7417C55.5386 10.2573 54.3579 11.1333 52.7646 11.1333C50.7461 11.1333 49.5273 9.80664 49.5273 7.66113C49.5273 5.53467 50.7588 4.13184 52.7012 4.13184C54.6118 4.13184 55.8052 5.4585 55.8052 7.50244V8.0166H51.1016V8.11182C51.1016 9.19092 51.7617 9.91455 52.7964 9.91455C53.5327 9.91455 54.0913 9.54639 54.2627 8.98779ZM56.8271 11V4.27148H58.3442V5.4458H58.376C58.6045 4.62695 59.1948 4.15723 59.9883 4.15723C60.1914 4.15723 60.3691 4.19531 60.4834 4.2207V5.64893C60.3501 5.59814 60.1089 5.56006 59.8423 5.56006C58.9473 5.56006 58.4077 6.1377 58.4077 7.0835V11H56.8271ZM61.2959 11V4.27148H62.813V5.4458H62.8447C63.0732 4.62695 63.6636 4.15723 64.457 4.15723C64.6602 4.15723 64.8379 4.19531 64.9521 4.2207V5.64893C64.8188 5.59814 64.5776 5.56006 64.311 5.56006C63.416 5.56006 62.8765 6.1377 62.8765 7.0835V11H61.2959ZM65.8027 11V4.27148H67.3833V11H65.8027ZM66.5898 3.38916C66.1011 3.38916 65.7139 3.0083 65.7139 2.53223C65.7139 2.0498 66.1011 1.66895 66.5898 1.66895C67.085 1.66895 67.4722 2.0498 67.4722 2.53223C67.4722 3.0083 67.085 3.38916 66.5898 3.38916ZM69.1479 2.69727H70.7285V4.27148H71.998V5.49023H70.7285V8.98779C70.7285 9.54004 70.9824 9.79395 71.541 9.79395C71.687 9.79395 71.8901 9.78125 71.9917 9.76221V10.9683C71.8203 11.0063 71.5093 11.0317 71.1729 11.0317C69.7192 11.0317 69.1479 10.5176 69.1479 9.20996V5.49023H68.1831V4.27148H69.1479V2.69727ZM75.9272 11.1333C73.9531 11.1333 72.6709 9.81934 72.6709 7.63574C72.6709 5.46484 73.9658 4.13184 75.9272 4.13184C77.8887 4.13184 79.1836 5.4585 79.1836 7.63574C79.1836 9.81934 77.9014 11.1333 75.9272 11.1333ZM75.9272 9.88281C76.9302 9.88281 77.5776 9.07031 77.5776 7.63574C77.5776 6.20752 76.9302 5.38867 75.9272 5.38867C74.9307 5.38867 74.2769 6.20752 74.2769 7.63574C74.2769 9.07031 74.9243 9.88281 75.9272 9.88281ZM80.1992 11V4.27148H81.7163V5.4458H81.748C81.9766 4.62695 82.5669 4.15723 83.3604 4.15723C83.5635 4.15723 83.7412 4.19531 83.8555 4.2207V5.64893C83.7222 5.59814 83.481 5.56006 83.2144 5.56006C82.3193 5.56006 81.7798 6.1377 81.7798 7.0835V11H80.1992ZM85.417 13.501C85.3281 13.501 84.8774 13.4946 84.7949 13.4756V12.2251C84.8774 12.2441 85.1567 12.2505 85.252 12.2505C85.9058 12.2505 86.2422 12.0029 86.4199 11.3809C86.4199 11.3618 86.5151 11.0254 86.5088 11.0063L84.1157 4.27148H85.8423L87.4292 9.6543H87.4546L89.0415 4.27148H90.7046L88.2734 11.2666C87.6958 12.936 86.9531 13.501 85.417 13.501ZM102.238 7.17871C102.238 9.66064 100.753 11.1523 98.2456 11.1523C95.5732 11.1523 93.9102 9.33691 93.9102 6.38525C93.9102 3.47803 95.5669 1.68799 98.2139 1.68799C100.296 1.68799 101.87 2.91943 102.168 4.75391H100.505C100.194 3.7002 99.3438 3.07812 98.2202 3.07812C96.5698 3.07812 95.5859 4.30322 95.5859 6.36621C95.5859 8.47363 96.6143 9.76221 98.2646 9.76221C99.6484 9.76221 100.607 8.88623 100.632 7.604L100.639 7.45166H98.4297V6.20117H102.238V7.17871ZM103.419 11V4.27148H104.936V5.4458H104.968C105.196 4.62695 105.787 4.15723 106.58 4.15723C106.783 4.15723 106.961 4.19531 107.075 4.2207V5.64893C106.942 5.59814 106.701 5.56006 106.434 5.56006C105.539 5.56006 105 6.1377 105 7.0835V11H103.419ZM110.611 11.1333C108.637 11.1333 107.354 9.81934 107.354 7.63574C107.354 5.46484 108.649 4.13184 110.611 4.13184C112.572 4.13184 113.867 5.4585 113.867 7.63574C113.867 9.81934 112.585 11.1333 110.611 11.1333ZM110.611 9.88281C111.614 9.88281 112.261 9.07031 112.261 7.63574C112.261 6.20752 111.614 5.38867 110.611 5.38867C109.614 5.38867 108.96 6.20752 108.96 7.63574C108.96 9.07031 109.608 9.88281 110.611 9.88281ZM120.85 4.27148V11H119.326V9.83203H119.294C118.939 10.6509 118.26 11.1206 117.238 11.1206C115.778 11.1206 114.851 10.1748 114.851 8.61963V4.27148H116.432V8.30225C116.432 9.26709 116.901 9.7876 117.79 9.7876C118.704 9.7876 119.269 9.14014 119.269 8.1626V4.27148H120.85ZM125.839 4.15723C127.546 4.15723 128.632 5.48389 128.632 7.63574C128.632 9.78125 127.553 11.1079 125.864 11.1079C124.887 11.1079 124.125 10.6382 123.757 9.88281H123.725V13.2153H122.145V4.27148H123.687V5.43311H123.719C124.1 4.65234 124.861 4.15723 125.839 4.15723ZM125.356 9.81299C126.378 9.81299 127.013 8.96875 127.013 7.63574C127.013 6.30908 126.378 5.4585 125.356 5.4585C124.373 5.4585 123.719 6.32812 123.712 7.63574C123.719 8.95605 124.373 9.81299 125.356 9.81299Z" fill="#6777FF" />
          <circle cx="6" cy="6" r="6" fill="#6777FF" />
          <path d="M6.70605 6.73438V8.90234H5.60742V6.73438H3.58105V5.65039H5.60742V3.5166H6.70605V5.65039H8.7373V6.73438H6.70605Z" fill="white" />
        </svg>
      </AddTerritoryGroupButton> */}
      <Modal
        visible={isOpen}
        onHide={() => {
          setIsOpen(false);
          setNewCountryGroupName('');
          setDropdownOpen(false);
          setEditId(null);
          setEditValue([]);
          setNewCountryGroupCheckboxes((prev) => {
            const newCountryGroupCheckboxes = { ...prev };
            countries.forEach(country => {
              newCountryGroupCheckboxes[country.id] = false;
            });
            return newCountryGroupCheckboxes;
          });
        }}
        dismissableMask
        showHeader={false}
        header={null}
        footer={(
          <Footer>
            <Button loading={countryGroupsCreating} disabled={isSaveDisabled} onClick={onSaveCountryGroupCheckboxes}>
              <FormattedMessage
                id="NewRelease.Publish.Save"
                defaultMessage="Save"
              />
            </Button>
          </Footer>
        )}
      >
        <NewCountryGroupNameContainer>
          <InputBox>
            <Input
              label={IntlStore.intl.formatMessage({
                id: "NewRelease.Publish.TerritoryGroupName",
                defaultMessage: "Territory Group Name"
              })}
              name="newCountryGroupName"
              placeholder={IntlStore.intl.formatMessage({
                id: "NewRelease.Publish.TerritoryGroupNamePlaceholder",
                defaultMessage: "Enter the name of the Territory Group"
              })}
              value={newCountryGroupName}
              onChange={(e) => setNewCountryGroupName(e.target.value)}
            />
          </InputBox>
          <Liner />
          <SearchInput
            name="search"
            value={value}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value)}
            placeholder={IntlStore.intl.formatMessage({
              id: "Artists.SearchCountryPlaceholder",
              defaultMessage: "Search Country"
            })}
          />
        </NewCountryGroupNameContainer>
        {!!countries.length && (
          <>
            <CheckAllCheckboxesContainer>
              <Checkbox
                name="all"
                label={IntlStore.intl.formatMessage({
                  id: "NewRelease.Publish.SelectAll",
                  defaultMessage: "Select All"
                })}
                checked={Object.entries(newCountryGroupCheckboxes).every((c) => c[1])}
                onChange={(event: CheckboxChangeEvent) => {
                  event.stopPropagation();
                  const checked = event.checked || false;
                  setNewCountryGroupCheckboxes(prev => {
                    const values: Record<string, boolean> = {};
                    Object.entries(prev).forEach(entry => {
                      values[entry[0]] = checked;
                    });
                    return values;
                  })
                }}
              />
            </CheckAllCheckboxesContainer>
            <CheckboxesContainer>
              {Object.entries(filteredCountryList).map(([continent, countries]) => (
                <CheckboxContainer key={continent}>
                  <GroupHeader onClick={() => onToggleGroupAccordion(continent)}>
                    <Checkbox
                      key={continent}
                      name={continent}
                      label={`${continent || IntlStore.intl.formatMessage({
                        id: "NewRelease.Publish.NoContinent",
                        defaultMessage: "No continent"
                      })} (${(countries as ContinentMapValue[]).length} ${IntlStore.intl.formatMessage({
                        id: "NewRelease.Publish.Total",
                        defaultMessage: "total"
                      })}, ${
                        (countries as ContinentMapValue[]).filter(c => !newCountryGroupCheckboxes[c.id]).length
                      } ${IntlStore.intl.formatMessage({
                        id: "NewRelease.Publish.Excluded",
                        defaultMessage: "excluded"
                      })})`}
                      
                      checked={(countries as ContinentMapValue[]).every((c) => newCountryGroupCheckboxes[c.id])}
                      onChange={(event: CheckboxChangeEvent) => {
                        event.stopPropagation();
                        const checked = event.checked || false;
                        setNewCountryGroupCheckboxes((prev) => {
                          const newCountryGroupCheckboxes = { ...prev };
                          countries.forEach(country => {
                            newCountryGroupCheckboxes[country.id] = checked;
                          });
                          return newCountryGroupCheckboxes;
                        });
                      }}
                    />
                    <CheckboxLabel>
                      <span>
                        {expandedGroups.includes(continent) && (
                          <svg width="11" height="7" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 6L6.3 1.06667C5.9 0.533333 5.1 0.533334 4.7 1.06667L0.999999 6" stroke="black" />
                          </svg>
                        )}
                        {!expandedGroups.includes(continent) && (
                          <svg width="11" height="7" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L4.7 5.93333C5.1 6.46667 5.9 6.46667 6.3 5.93333L10 1" stroke="black" />
                          </svg>
                        )}
                      </span>
                    </CheckboxLabel>
                  </GroupHeader>
                  {expandedGroups.includes(continent) && (
                    <CountryCheckboxes>
                      {countries.map((country) => (
                        <Checkbox
                          key={country.id}
                          name={country.id}
                          label={country.name}
                          checked={newCountryGroupCheckboxes[country.id]}
                          onChange={(event: CheckboxChangeEvent) => {
                            event.stopPropagation();
                            const checked = event.checked || false;
                            setNewCountryGroupCheckboxes(prev => ({ ...prev, [country.id]: checked }));
                          }}
                        />
                      ))}
                    </CountryCheckboxes>
                  )}
                </CheckboxContainer>
              ))}
            </CheckboxesContainer>
          </>
        )}
      </Modal>
    </Container>
  </>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 32px 0 66px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    margin: 20px 0 40px;
  }
`;

const SelectsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5px;
  width: 80%;

  & > * {
    flex: 1;
    max-width: 253px;
  }

  @media (max-width: 768px) {
    width: 100%;
    align-items: stretch;
    flex-direction: column;
    gap: 12px;

    & > * {
      max-width: none;
      width: 100%;
    }
  }
`;

const LockedCountrySelect = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 253px;

  @media (max-width: 768px) {
    max-width: none;
  }
`;

const LockedLabel = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: #000000;
`;

const LockedInput = styled.div`
  width: 100%;
  min-height: 44px;
  border: 1px solid rgba(0, 0, 0, 0.25);
  border-radius: 10px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.6);
  user-select: none;
`;

const AddTerritoryGroupButton = styled.div`
  cursor: pointer;
`;

const DistributionCountryOption = styled.div`
  display: flex;
  justify-content: space-between;
  width: 260px;
`;

const DistributionCountryOptionLabel = styled.div`
  flex: 1;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #00000033;
  padding: 12px 16px 0 24px;
  margin: 0 -24px;
`;

const NewCountryGroupNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
  margin-top: 27px;
`;

const CheckboxesContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckAllCheckboxesContainer = styled.div`
  padding: 20px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  width: 800px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  padding: 20px 0;
`;

const CountryCheckboxes = styled.div`
  width:70%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  width: 800px;

  @media (max-width: 768px) {
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 12px;
  }

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxContainer = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

const CheckboxLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const UGCTitle = styled.div`
  display:flex;
  align-items: center;
  gap: 4px;
  font-family: SF Pro Display;
  font-weight: 500;
  font-style: Medium;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 18px;
  letter-spacing: 0%;
  vertical-align: middle;
  color:#000000;

    span{
    font-family: SF Pro Display;
    font-weight: 500;
    font-style: Medium;
    font-size: 14px;
    leading-trim: NONE;
    line-height: 18px;
    letter-spacing: 0%;
    vertical-align: middle;
    color:#6777FF;
  }

`
const PlatformOption = styled.div`
  display: flex;
  justify-content: space-between;
  width: 260px;
`;

const PlatformOptionLabel = styled.div`
  flex: 1;
`

const InputBox = styled.div`
  width: 70%;
`
const SearchInput = styled(Input)`
  height: 43px;
  width: 100%;
  overflow: hidden;
  outline: none;  
  &:focus {
    outline: none;  
    box-shadow: none;
  }
  &:active {
    outline: none;  
    box-shadow: none;
  }
`;
const Liner = styled.div`
  width: 100%;
  height: 1px;
  background: #00000033;
`